import { z } from "zod";

/**
 * The expert report and its entry, **one contract for the doors and for every client** (`GENERIC-DOOR-SWEEP`, Arc 4,
 * group B, A48): `GET /cabinet/demands/{id}`, `PUT /cabinet/demands/{id}/entry`, `POST …/entry/return`,
 * `POST …/entry/visa`, `POST …/entry/correction`, `GET /cabinet/entries`, `POST /expertise/requests/{id}/report`,
 * `GET /expertise/reports/{id}`, `GET /expertise/grid` and `GET /me/vehicles/{id}/rapport`.
 *
 * Until this file these doors answered `unknown`, `Record<string, unknown>` or objects the controllers widened, and
 * expert-web, console-web and dealer-web narrowed by hand. **Amounts are whole francs** (`INTEGER` columns): every
 * `*Amount` is `z.number().int()`; a kilometre reading is an integer too. **The two documents on the dealer's report
 * (the cabinet's paper, Hakili's sheet) carry a presigned `url` minted for the answer, and the door gives no expiry for
 * it**: a string, nothing more.
 *
 * Two shapes of one report are kept apart on purpose: {@link reportSchema} is what the visa and the cabinet's facts
 * answer, {@link staffReportSchema} adds the line that sits under the findings on the staff's screens
 * (`mention`, WF-CON-34).
 */

/** The three verdicts the grid decides (`02`, 2.4.5): the expert constates, the grid decides. */
export const reportVerdictSchema = z.enum(["conforme", "ecartprix", "nonconforme"]);

/** One finding of a report: a criterion of the grid and the expert's detail. */
export const reportFindingSchema = z.strictObject({
  code: z.string(),
  label: z.string(),
  family: z.string(),
  detail: z.string().nullable(),
});

/** A filed report and the verdict the grid derived from it. */
export const reportSchema = z.strictObject({
  reportId: z.string(),
  demandId: z.string(),
  points: z.number().int(),
  totalPoints: z.number().int(),
  verdict: reportVerdictSchema,
  /** The gap between the estimate and the asked price, in percent; `null` when there is no asked price to compare. */
  gapPct: z.number().nullable(),
  demandReference: z.string(),
  /** The expert's estimated market value, whole FCFA. */
  estimatedValueAmount: z.number().int(),
  /** The dealer's asked price, whole FCFA. */
  askedPriceAmount: z.number().int(),
  observations: z.string(),
  gridVersion: z.number().int(),
  entryMode: z.string(),
  authorLabel: z.string(),
  receptionChannel: z.string().nullable(),
  reportedAt: z.string(),
  version: z.number().int(),
  findings: z.array(reportFindingSchema),
  /** Only on `ecartprix`: the sentence with both amounts (`05`, 2.5.12). */
  ecartPhrase: z.string().nullable(),
});

export const staffReportSchema = reportSchema.extend({ mention: z.string() });

/** `POST /expertise/requests/{id}/report` and `GET /expertise/reports/{id}`. */
export const staffReportAnswerSchema = z.strictObject({ report: staffReportSchema });

/** `POST /cabinet/demands/{id}/entry/visa`: the report stands with the responsable's visa. */
export const cabinetVisaAnswerSchema = z.strictObject({ report: reportSchema });

/** The expert's entry, under the visa. */
export const cabinetEntrySchema = z.strictObject({
  demandId: z.string(),
  status: z.string(),
  chassisNumberRead: z.string().nullable(),
  chassisMatches: z.boolean().nullable(),
  plateRead: z.string().nullable(),
  plateMatches: z.boolean().nullable(),
  carteGriseFound: z.boolean().nullable(),
  vehicleYearRead: z.number().int().nullable(),
  vehicleYearMatches: z.boolean().nullable(),
  /** The odometer as read, whole kilometres. */
  odometerReadKm: z.number().int().nullable(),
  observed: z.array(z.strictObject({ code: z.string(), detail: z.string().nullable() })),
  estimatedValueAmount: z.number().int().nullable(),
  observations: z.string(),
  photos: z.array(z.strictObject({ view: z.string(), documentId: z.string() })),
  attestedAt: z.string().nullable(),
  enteredByUserId: z.string(),
  enteredByName: z.string().nullable(),
  enteredAt: z.string(),
  returnedNote: z.string().nullable(),
  returnedAt: z.string().nullable(),
});

/** `PUT …/entry`, `POST …/entry/return`, `POST …/entry/correction`. */
export const cabinetEntryAnswerSchema = z.strictObject({ entry: cabinetEntrySchema });

/** One line of the responsable's desk: the entry and the demand it is for. */
export const cabinetEntryLineSchema = cabinetEntrySchema.extend({
  reference: z.string(),
  vehicleLabel: z.string(),
  sellerName: z.string(),
});

export const cabinetEntriesAnswerSchema = z.strictObject({ entries: z.array(cabinetEntryLineSchema) });

/** The grid as the entry form renders it: rows, not a constant in a bundle. */
export const gridCriterionSchema = z.strictObject({
  code: z.string(),
  label: z.string(),
  helpText: z.string(),
});

const gridFamilyBase = {
  code: z.string(),
  title: z.string(),
  helpText: z.string(),
  isEliminating: z.boolean(),
  isCheckable: z.boolean(),
  criteria: z.array(gridCriterionSchema),
};

/** The grid inside the cabinet's facts: version and families. */
export const entryGridSchema = z.strictObject({
  version: z.number().int(),
  families: z.array(z.strictObject(gridFamilyBase)),
});

/** `GET /expertise/grid`: the grid in force with its weights. */
export const expertiseGridAnswerSchema = z.strictObject({
  version: z.number().int(),
  label: z.string(),
  totalPoints: z.number().int(),
  tolerancePct: z.number(),
  /** The repair amount above which a repair is major, whole FCFA. */
  majorRepairThresholdAmount: z.number().int(),
  reportValidityDays: z.number().int(),
  families: z.array(z.strictObject({ ...gridFamilyBase, pointsRemoved: z.number().int() })),
  mention: z.string(),
});

/** The demand as the cabinet reads it: the vehicle's facts, with no asked price (decision A). */
export const cabinetDemandSchema = z.strictObject({
  id: z.string(),
  reference: z.string(),
  vehicleLabel: z.string(),
  vehiclePlate: z.string().nullable(),
  vehicleRegistrationDeclared: z.boolean().nullable(),
  vehicleYearDeclared: z.number().int().nullable(),
  vehicleMileageKm: z.number().int(),
  vehicleColour: z.string(),
  sellerName: z.string(),
  clientName: z.string().nullable(),
  isStockControl: z.boolean(),
  status: z.string(),
});

/** `GET /cabinet/demands/{id}`: the demand, the grid, the entry as it stands, the report once sent (decision B). */
export const cabinetDemandFactsAnswerSchema = z.strictObject({
  demand: cabinetDemandSchema,
  grid: entryGridSchema,
  entry: cabinetEntrySchema.nullable(),
  report: reportSchema.nullable(),
});

/** One of the report's two documents as the dealer opens it: a presigned read minted for this answer. */
export const reportDocumentSchema = z.strictObject({
  fileName: z.string(),
  mimeType: z.string(),
  /** The SHA-256 of the bytes, computed at reception. */
  contentHash: z.string(),
  url: z.string(),
});

/** The standing report as the dealer opens it on his own car: nothing of the buyer. */
export const dealerReportSchema = z.strictObject({
  cabinetName: z.string(),
  version: z.number().int(),
  reportedAt: z.string(),
  validUntil: z.string(),
  entryMode: z.string(),
  authorLabel: z.string(),
  visaByName: z.string().nullable(),
  visaAt: z.string().nullable(),
  paper: reportDocumentSchema.nullable(),
  sheet: reportDocumentSchema.nullable(),
});

/** `GET /me/vehicles/{id}/rapport`: `null` while no report stands on the car. */
export const dealerReportAnswerSchema = z.strictObject({ report: dealerReportSchema.nullable() });

export type ReportVerdict = z.infer<typeof reportVerdictSchema>;
export type ReportFinding = z.infer<typeof reportFindingSchema>;
export type Report = z.infer<typeof reportSchema>;
export type StaffReport = z.infer<typeof staffReportSchema>;
export type StaffReportAnswer = z.infer<typeof staffReportAnswerSchema>;
export type CabinetVisaAnswer = z.infer<typeof cabinetVisaAnswerSchema>;
export type CabinetEntry = z.infer<typeof cabinetEntrySchema>;
export type CabinetEntryAnswer = z.infer<typeof cabinetEntryAnswerSchema>;
export type CabinetEntryLine = z.infer<typeof cabinetEntryLineSchema>;
export type CabinetEntriesAnswer = z.infer<typeof cabinetEntriesAnswerSchema>;
export type GridCriterion = z.infer<typeof gridCriterionSchema>;
export type EntryGrid = z.infer<typeof entryGridSchema>;
export type ExpertiseGridAnswer = z.infer<typeof expertiseGridAnswerSchema>;
export type CabinetDemand = z.infer<typeof cabinetDemandSchema>;
export type CabinetDemandFactsAnswer = z.infer<typeof cabinetDemandFactsAnswerSchema>;
export type ReportDocument = z.infer<typeof reportDocumentSchema>;
export type DealerReport = z.infer<typeof dealerReportSchema>;
export type DealerReportAnswer = z.infer<typeof dealerReportAnswerSchema>;
